export interface PortalRole {
  id: string;
  name: string;
  description: string;
  icon: 'teacher' | 'student' | 'parent' | 'admin';
  permissions: string[];
  viewMode: 'personal' | 'class' | 'full';
}

export interface PreviewUser {
  id: string;
  name: string;
  role: string;
  email: string;
  avatar?: string;
  yearGroup?: string;
  department?: string;
  classes?: string[];
}

export interface TimetableSlot {
  day: string;
  period: number;
  subject: string;
  subjectColor: string;
  teacher: string;
  room: string;
  classGroup?: string;
}

export const portalRoles: PortalRole[] = [
  {
    id: 'teacher',
    name: 'Teacher',
    description: 'View personal teaching timetable and department schedules',
    icon: 'teacher',
    permissions: ['view_personal', 'view_department', 'view_room_availability'],
    viewMode: 'personal',
  },
  {
    id: 'student',
    name: 'Student',
    description: 'View class timetable and personal schedule',
    icon: 'student',
    permissions: ['view_personal', 'view_class'],
    viewMode: 'class',
  },
  {
    id: 'parent',
    name: 'Parent/Guardian',
    description: 'View linked student timetables',
    icon: 'parent',
    permissions: ['view_linked_students'],
    viewMode: 'personal',
  },
  {
    id: 'admin',
    name: 'Administrator',
    description: 'Full access to all timetables and management features',
    icon: 'admin',
    permissions: ['view_all', 'manage_timetables', 'export', 'finalize'],
    viewMode: 'full',
  },
];

export const mockPreviewUsers: Record<string, PreviewUser[]> = {
  teacher: [
    {
      id: 'teacher-preview-1',
      name: 'Ms. Sarah Johnson',
      role: 'teacher',
      email: 's.johnson@school.edu',
      department: 'English',
      classes: ['7A', '8B', '9C', '10A', '11B'],
    },
    {
      id: 'teacher-preview-2',
      name: 'Mr. James Williams',
      role: 'teacher',
      email: 'j.williams@school.edu',
      department: 'Mathematics',
      classes: ['7B', '8A', '9A', '10B', '11A'],
    },
    {
      id: 'teacher-preview-3',
      name: 'Dr. Emily Brown',
      role: 'teacher',
      email: 'e.brown@school.edu',
      department: 'Science',
      classes: ['10A', '10B', '11A', '11B', '12A'],
    },
  ],
  student: [
    {
      id: 'student-preview-1',
      name: 'Oliver Smith',
      role: 'student',
      email: 'o.smith@school.edu',
      yearGroup: 'Year 10',
      classes: ['10A'],
    },
    {
      id: 'student-preview-2',
      name: 'Emma Wilson',
      role: 'student',
      email: 'e.wilson@school.edu',
      yearGroup: 'Year 11',
      classes: ['11B'],
    },
    {
      id: 'student-preview-3',
      name: 'Jack Taylor',
      role: 'student',
      email: 'j.taylor@school.edu',
      yearGroup: 'Year 7',
      classes: ['7A'],
    },
  ],
  parent: [
    {
      id: 'parent-preview-1',
      name: 'Mr. David Smith',
      role: 'parent',
      email: 'd.smith@email.com',
    },
    {
      id: 'parent-preview-2',
      name: 'Mrs. Lucy Wilson',
      role: 'parent',
      email: 'l.wilson@email.com',
    },
  ],
  admin: [
    {
      id: 'admin-preview-1',
      name: 'John Smith (Deputy Head)',
      role: 'admin',
      email: 'j.smith@school.edu',
    },
  ],
};

// Mock timetable data for preview
export const generateMockTimetable = (role: string, userId: string): TimetableSlot[] => {
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  const periods = [1, 2, 3, 4, 5];
  
  const subjectColors: Record<string, string> = {
    English: '#ef4444',
    Mathematics: '#3b82f6',
    Science: '#10b981',
    History: '#f59e0b',
    Geography: '#8b5cf6',
    PE: '#ec4899',
    Art: '#06b6d4',
    Music: '#84cc16',
    'Free Period': '#e5e7eb',
  };

  const slots: TimetableSlot[] = [];
  const subjects = Object.keys(subjectColors);

  days.forEach((day) => {
    periods.forEach((period) => {
      // Generate varied timetable based on day and period
      const subjectIndex = (days.indexOf(day) + period) % (subjects.length - 1);
      const subject = Math.random() > 0.15 ? subjects[subjectIndex] : 'Free Period';
      
      slots.push({
        day,
        period,
        subject,
        subjectColor: subjectColors[subject],
        teacher: role === 'teacher' ? 'You' : `Teacher ${period}`,
        room: `Room ${100 + ((days.indexOf(day) * 5 + period) % 20)}`,
        classGroup: role === 'student' ? undefined : `${7 + (period % 7)}${String.fromCharCode(65 + (days.indexOf(day) % 3))}`,
      });
    });
  });

  return slots;
};

export const portalAccessStats = {
  totalActiveUsers: 1247,
  teacherAccess: 85,
  studentAccess: 892,
  parentAccess: 270,
  lastPublished: '2025-01-16T10:00:00Z',
  publishedVersion: 'v3',
};

export const accessSettings = {
  teacherAccessEnabled: true,
  studentAccessEnabled: true,
  parentAccessEnabled: true,
  publicAccessEnabled: false,
  requireAuth: true,
  showConflicts: false,
  showNotes: true,
};

